/* ==========================================================================
   Raptor Racing "lively" layer, theme v7.1 (motion review, owner approved
   4 Oct 2026). Loaded with defer from layout/theme.liquid after rr-motion.js.
   CSS lives in rr-brand.css section 8.

   Brand rules (guide v2.17 section 12): brand curve cubic-bezier(.2,.8,.2,1),
   150 / 250 / 450 ms, entrances play once, transform and opacity only,
   nothing moves near prices or fitment while people read, and nothing here
   runs under prefers-reduced-motion. Everything this file touches is fully
   visible and usable when it never runs: the classes that hide or move
   anything are added here, never in the markup.

   (E1) sections rise in as they first scroll into view (below the fold only)
   (E2) page-to-page transitions: the tapped product photo is named so the
        browser's cross-document view transition grows it into the product
        page photo (the cross-fade itself is CSS only)
   (E3) on add to cart a small copy of the product photo flies to the cart
   (E5) proof numbers ("750,000+ parts", "500+ brands") count up once
   (E10) the theme's AJAX collection filtering fades the new results in
   (E11) homepage hero: a slow push-in on each slide's photo, once per slide
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var win = window;
  var mq = win.matchMedia ? win.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var EASE = 'cubic-bezier(.2,.8,.2,1)';
  var SLOW = 450;

  function reduced() { return !!(mq && mq.matches); }
  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }
  function ready(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }
  function bodyIs(re) { return !!(doc.body && re.test(doc.body.className)); }
  function onScreen(r) { return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < win.innerHeight && r.right > 0 && r.left < win.innerWidth; }

  /* The brand curve as a function, for the count-up (cubic-bezier with
     x1 .2, y1 .8, x2 .2, y2 1; solved for x by Newton's method) */
  function brandEase(t) {
    var x1 = 0.2, y1 = 0.8, x2 = 0.2, y2 = 1;
    var cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    var cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    var u = t;
    for (var i = 0; i < 8; i++) {
      var x = ((ax * u + bx) * u + cx) * u - t;
      var d = (3 * ax * u + 2 * bx) * u + cx;
      if (Math.abs(x) < 1e-5 || !d) break;
      u -= x / d;
    }
    u = Math.max(0, Math.min(1, u));
    return ((ay * u + by) * u + cy) * u;
  }

  /* ------------------------------------------------------------------------
     (E1) Scroll reveal. Tiles below the first screen at load get .rr-rv
     (12px down, transparent) and rise in over 450ms when they first come
     into view, 60ms apart within one batch (at most 6 steps). The classes
     come off when the rise ends, so the tiles' own hover motion is back.
     Never on product, cart or checkout pages, never above the fold.
     ------------------------------------------------------------------------ */
  var REVEAL = [
    '.collection-list__thumbnail', '.collection-list-main .thumbnail', '.logo-list__item',
    '.brands-dir__carousel-item', '.rr-lc', '.rr-bband', '.icon-bar__block'
  ].join(',');

  function initReveal() {
    if (reduced() || !('IntersectionObserver' in win)) return;
    if (bodyIs(/(^|\s)(product|cart|customers|gift_card)(\s|$|-)/)) return;
    var vh = win.innerHeight;
    var io = new IntersectionObserver(function (entries) {
      var batch = entries.filter(function (en) { return en.isIntersecting; });
      batch.forEach(function (en, i) {
        var el = en.target;
        io.unobserve(el);
        el.style.setProperty('--rr-rv-d', Math.min(i, 6) * 60 + 'ms');
        el.classList.add('rr-rv-in');
        var done = function (ev) {
          if (ev && ev.target !== el) return;
          el.classList.remove('rr-rv', 'rr-rv-in');
          el.style.removeProperty('--rr-rv-d');
          el.removeEventListener('transitionend', done);
        };
        el.addEventListener('transitionend', done);
        win.setTimeout(done, SLOW + 6 * 60 + 200);   /* safety: never stay hidden */
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    each(doc.querySelectorAll(REVEAL), function (el) {
      if (el.closest('header, .header-section, #mobile-header, .mobile-menu')) return;
      var r = el.getBoundingClientRect();
      if (!r.width || r.top < vh) return;   /* above the fold or hidden: leave it alone */
      el.classList.add('rr-rv');
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     (E2) Page-to-page transitions. rr-brand.css 8 opts every page into the
     browser's cross-document view transition (a 250ms cross-fade); here the
     product photo the shopper taps is named rr-product so it grows into the
     product page's first photo, which carries the same name. Browsers
     without view transitions just navigate.
     ------------------------------------------------------------------------ */
  var VT_OK = !!(win.CSS && CSS.supports && CSS.supports('view-transition-name: rr-a'));
  var CARD = '.rr-card, .rr-cav__it, .rr-pair, .product-wrap, .thumbnail, [class*="product-card"], [class*="product-item"], li, article';

  function clearNames() {
    each(doc.querySelectorAll('[data-rr-vt]'), function (el) {
      el.style.viewTransitionName = '';
      el.removeAttribute('data-rr-vt');
    });
  }

  function onCardClick(e) {
    if (!VT_OK || reduced() || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
    if (!a || (a.target && a.target !== '_self') || a.host !== win.location.host) return;
    var card = a.closest(CARD) || a;
    var img = null;
    each(card.querySelectorAll('img'), function (im) {
      if (!img && onScreen(im.getBoundingClientRect())) img = im;
    });
    if (!img) return;
    clearNames();
    /* on a product page its own main photo already carries the name */
    each(doc.querySelectorAll('.product-gallery__main img[fetchpriority="high"]'), function (main) {
      if (main !== img) { main.style.viewTransitionName = 'none'; main.setAttribute('data-rr-vt', ''); }
    });
    img.style.viewTransitionName = 'rr-product';
    img.setAttribute('data-rr-vt', '');
  }

  /* ------------------------------------------------------------------------
     (E3) Fly to cart. After a successful add (gv:cart:added, dispatched by
     rr-motion.js d4 and raptor-recs.js), a 56px copy of the product photo
     flies in a straight line to the cart icon on the brand curve and fades
     as it lands; the count pop (rr-motion.js c) follows as the number
     changes. Only after the shopper pressed the button.
     ------------------------------------------------------------------------ */
  function cartTarget() {
    var best = null;
    each(doc.querySelectorAll('.header-cart__icon, .header-cart'), function (el) {
      if (best) return;
      var r = el.getBoundingClientRect();
      if (onScreen(r)) best = r;
    });
    return best;
  }

  function sourceImage(button) {
    var pick = function (list) {
      var found = null;
      each(list, function (im) { if (!found && onScreen(im.getBoundingClientRect()) && (im.currentSrc || im.src)) found = im; });
      return found;
    };
    var card = button.closest('.rr-card, .rr-cav__it, .rr-pair');
    if (card) return pick(card.querySelectorAll('img'));
    if (button.closest('form[action*="/cart/add"]')) {
      return pick(doc.querySelectorAll('.product-gallery__main .is-selected img, .product-gallery__main img'));
    }
    return null;
  }

  function galleryImage() {
    var found = null;
    each(doc.querySelectorAll('.product-gallery__main .is-selected img, .product-gallery__main img'), function (im) {
      if (!found && (im.currentSrc || im.src) && !/^data:/.test(im.currentSrc || im.src)) found = im;
    });
    return found;
  }

  function onAdded(e) {
    if (reduced()) return;
    var detail = (e && e.detail) || {};
    var button = detail.button;
    if (!button || !button.getBoundingClientRect) return;
    var to = cartTarget();
    if (!to) return;
    var bRect = button.getBoundingClientRect();
    var img;
    var from;
    if (detail.echo) {
      /* a sticky bar press (rr-pdp.js): fly from the sticky button with the
         product photo, even though the gallery is off screen */
      img = galleryImage();
      from = bRect;
    } else {
      img = sourceImage(button);
      from = img ? img.getBoundingClientRect() : bRect;
      if (!onScreen(from)) from = bRect;
      /* the main button pressed on the shopper's behalf by a sticky bar is
         off screen: the sticky bar's own (echo) event flies instead */
      if (!onScreen(from)) return;
    }
    if (!img) return;   /* never fly an empty box */
    var S = 56;
    var x0 = from.left + from.width / 2 - S / 2;
    var y0 = from.top + from.height / 2 - S / 2;
    var dx = to.left + to.width / 2 - (x0 + S / 2);
    var dy = to.top + to.height / 2 - (y0 + S / 2);
    var f = doc.createElement(img ? 'img' : 'span');
    f.className = 'rr-flyer';
    f.setAttribute('aria-hidden', 'true');
    if (img) { f.src = img.currentSrc || img.src; f.alt = ''; }
    f.style.left = Math.round(x0) + 'px';
    f.style.top = Math.round(y0) + 'px';
    doc.body.appendChild(f);
    if (!f.animate) { f.parentNode.removeChild(f); return; }
    var a = f.animate([
      { transform: 'none', opacity: 1 },
      { transform: 'translate(' + dx * 0.7 + 'px,' + dy * 0.7 + 'px) scale(.5)', opacity: 1, offset: 0.7 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.25)', opacity: 0 }
    ], { duration: SLOW, easing: EASE });
    a.onfinish = a.oncancel = function () { if (f.parentNode) f.parentNode.removeChild(f); };
  }

  /* ------------------------------------------------------------------------
     (E5) Count-up. In the homepage story band and the footer icon bar, each
     "750,000+" / "500+" counts up from 0 over 450ms on the brand curve the
     first time it scrolls into view (below the fold only). The final number
     is in the page text throughout; the number's box keeps its final width
     while it counts, so the sentence around it never moves.
     ------------------------------------------------------------------------ */
  var COUNT_IN = '.rr-bband__text p, .icon-bar__block';
  var NUM = /(\d{1,3}(?:,\d{3})+|\d{2,})(?=\+)/g;

  function wrapNumbers(el) {
    var walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    var n;
    while ((n = walker.nextNode())) { if (NUM.test(n.nodeValue)) nodes.push(n); NUM.lastIndex = 0; }
    var spans = [];
    nodes.forEach(function (node) {
      var text = node.nodeValue;
      var frag = doc.createDocumentFragment();
      var last = 0;
      text.replace(NUM, function (m, num, idx) {
        if (idx > 0 && /[$€£¢]/.test(text.charAt(idx - 1))) return m;   /* a price never counts */
        if (idx > last) frag.appendChild(doc.createTextNode(text.slice(last, idx)));
        var s = doc.createElement('span');
        s.className = 'rr-count';
        s.setAttribute('data-to', num.replace(/,/g, ''));
        s.textContent = num;
        frag.appendChild(s);
        spans.push(s);
        last = idx + m.length;
        return m;
      });
      if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
    return spans;
  }

  function countUp(s) {
    var to = parseInt(s.getAttribute('data-to'), 10);
    if (!to) return;
    var final = s.textContent;
    s.style.width = s.getBoundingClientRect().width + 'px';
    s.classList.add('is-counting');
    var t0 = null;
    var step = function (ts) {
      if (t0 === null) t0 = ts;
      var k = Math.min(1, (ts - t0) / SLOW);
      s.textContent = k < 1 ? Math.round(to * brandEase(k)).toLocaleString('en-CA') : final;
      if (k < 1) win.requestAnimationFrame(step);
      else { s.classList.remove('is-counting'); s.style.width = ''; }
    };
    s.textContent = '0';
    win.requestAnimationFrame(step);
  }

  function initCount() {
    if (reduced() || !('IntersectionObserver' in win) || !win.requestAnimationFrame) return;
    var vh = win.innerHeight;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        countUp(en.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 1 });
    each(doc.querySelectorAll(COUNT_IN), function (el) {
      var r = el.getBoundingClientRect();
      if (!r.width || r.top < vh) return;
      wrapNumbers(el).forEach(function (s) { io.observe(s); });
    });
  }

  /* ------------------------------------------------------------------------
     (E10) The theme's AJAX collection filtering (z__jsCollection.js) swaps
     the grid inside [data-collection-main]; the new results fade in together
     over 250ms (no stagger: shoppers want them fast). XCloud's own filters
     load a new page, which the page transition (E2) covers.
     ------------------------------------------------------------------------ */
  function initResults() {
    if (reduced() || !win.MutationObserver) return;
    each(doc.querySelectorAll('[data-collection-main]'), function (main) {
      new MutationObserver(function (list) {
        list.forEach(function (m) {
          each(m.addedNodes, function (n) {
            if (n.nodeType !== 1 || !n.classList || !n.classList.contains('collection-matrix__wrapper')) return;
            n.classList.add('rr-fres');
            n.addEventListener('animationend', function () { n.classList.remove('rr-fres'); }, { once: true });
          });
        });
      }).observe(main, { childList: true });
    });
  }

  /* ------------------------------------------------------------------------
     (E11) Homepage hero, slow push-in (owner approved 4 Oct 2026): each
     slide's photo scales 1 to 1.04 over the autoplay interval, once per
     time the slide shows, paused with the slideshow (hover, focus, off
     screen, background tab). The class comes off a slide only once it has
     left the screen, so nothing snaps back in view.
     ------------------------------------------------------------------------ */
  function initKenBurns() {
    if (reduced()) return;
    each(doc.querySelectorAll('section-slideshow'), function (el) {
      if (el.__rrKb || el.getAttribute('enable-autoplay') !== 'true') return;
      el.__rrKb = true;
      var secs = parseInt(el.getAttribute('autoplay-interval'), 10) || 7;
      el.style.setProperty('--rr-kb-dur', (secs * 1000 + 450) + 'ms');   /* the interval plus the 450ms slide */
      var start = function () {
        var sw = el.swiper;
        var slide = sw && sw.slides && sw.slides[sw.activeIndex];
        if (!slide) return;
        slide.classList.remove('rr-kb');
        void slide.offsetWidth;
        slide.classList.add('rr-kb');
      };
      var tidy = function () {
        var sw = el.swiper;
        if (!sw || !sw.slides) return;
        each(sw.slides, function (s, i) { if (i !== sw.activeIndex) s.classList.remove('rr-kb'); });
      };
      var hook = function () {
        if (!el.swiper || typeof el.swiper.on !== 'function') return false;
        el.swiper.on('slideChangeTransitionStart', start);
        el.swiper.on('slideChangeTransitionEnd', tidy);
        start();
        return true;
      };
      if (!hook()) {
        var tries = 0;
        var t = win.setInterval(function () { if (hook() || ++tries > 40) win.clearInterval(t); }, 250);
      }
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  doc.addEventListener('click', onCardClick, true);
  win.addEventListener('pageshow', clearNames);   /* back/forward cache: drop the last tap's name */
  doc.addEventListener('gv:cart:added', onAdded);
  ready(function () {
    initResults();
    initKenBurns();
    /* after the first layout settles, so "below the fold" is measured right */
    win.setTimeout(function () { initReveal(); initCount(); }, 0);
  });
  doc.addEventListener('shopify:section:load', function () { initKenBurns(); });
})();
